import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, Plus, TrendingUp, PiggyBank, HandCoins, RefreshCw, Pencil, Sparkles, Archive } from "lucide-react";
import UsageTip from "@/components/UsageTip";
import MoneyInput from "@/components/MoneyInput";

const cop = (v) => `$ ${(Number(v) || 0).toLocaleString("es-CO")}`;

export default function SociosPage() {
  const { user } = useAuth();
  const isDirector = user?.role === "admin";
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [openCrear, setOpenCrear] = useState(false);
  const [openEditar, setOpenEditar] = useState(null); // socio
  const [openAporte, setOpenAporte] = useState(null); // socio
  const [openDist, setOpenDist] = useState(false);
  const [openRetiro, setOpenRetiro] = useState(null); // socio
  const [openExtracto, setOpenExtracto] = useState(null); // socio
  const [extracto, setExtracto] = useState(null);
  const [form, setForm] = useState({ nombre: "", email: "", user_id: "", aporte_inicial: 0, porcentaje: 0, retiro_mensual_tope: 0 });
  const [editForm, setEditForm] = useState({ nombre: "", email: "", user_id: "", porcentaje: 0, retiro_mensual_tope: 0, activo: true });
  const [aporteForm, setAporteForm] = useState({ monto: "", notas: "" });
  const [distForm, setDistForm] = useState({ utilidad_neta: "", notas: "" });
  const [sugerida, setSugerida] = useState(null);
  const [modoSugerido, setModoSugerido] = useState("causacion");
  const [retiroForm, setRetiroForm] = useState({ monto: "", notas: "" });
  const [openCierre, setOpenCierre] = useState(false);
  const [cierre, setCierre] = useState(null);
  const [cierreYear, setCierreYear] = useState(new Date().getFullYear());
  const [cierreLoading, setCierreLoading] = useState(false);

  const abrirCierreAnual = async (yr) => {
    setOpenCierre(true);
    setCierreLoading(true);
    setCierre(null);
    try {
      const r = await api.get(`/socios/cierre-anual`, { params: { year: yr || cierreYear } });
      setCierre(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el cierre");
    } finally {
      setCierreLoading(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get("/socios");
      setData(r.data);
      const u = await api.get("/team").catch(() => ({ data: [] }));
      setUsers(u.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  }, []);
  useEffect(() => { if (isDirector) load(); }, [load, isDirector]);

  const crear = async () => {
    if (!form.nombre.trim() || !form.porcentaje) return toast.error("Nombre y porcentaje obligatorios");
    try {
      await api.post("/socios", {
        nombre: form.nombre.trim(),
        email: form.email || null,
        user_id: form.user_id || null,
        aporte_inicial: Number(form.aporte_inicial) || 0,
        porcentaje: Number(form.porcentaje),
        retiro_mensual_tope: Number(form.retiro_mensual_tope) || 0,
      });
      toast.success("Socio creado");
      setOpenCrear(false);
      setForm({ nombre: "", email: "", user_id: "", aporte_inicial: 0, porcentaje: 0, retiro_mensual_tope: 0 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const abrirEditar = (s) => {
    setEditForm({
      nombre: s.nombre || "",
      email: s.email || "",
      user_id: s.user_id || "",
      porcentaje: s.porcentaje || 0,
      retiro_mensual_tope: s.retiro_mensual_tope || 0,
      activo: s.activo !== false,
      _pct_original: s.porcentaje || 0,
    });
    setOpenEditar(s);
  };

  const guardarEdicion = async () => {
    if (!editForm.nombre.trim()) return toast.error("Nombre obligatorio");
    const pctCambio = Number(editForm.porcentaje) !== Number(editForm._pct_original);
    if (pctCambio) {
      const ok = window.confirm(
        `Vas a cambiar el % de participación de ${editForm._pct_original}% a ${editForm.porcentaje}%. Esto afecta futuras distribuciones. ¿Confirmas?`
      );
      if (!ok) return;
    }
    try {
      await api.patch(`/socios/${openEditar.id}`, {
        nombre: editForm.nombre.trim(),
        email: editForm.email || null,
        user_id: editForm.user_id || null,
        porcentaje: Number(editForm.porcentaje),
        retiro_mensual_tope: Number(editForm.retiro_mensual_tope) || 0,
        activo: !!editForm.activo,
      });
      toast.success("Socio actualizado");
      setOpenEditar(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const registrarAporte = async () => {
    if (!aporteForm.monto || Number(aporteForm.monto) <= 0) return toast.error("Monto obligatorio");
    try {
      await api.post(`/socios/${openAporte.id}/aporte`, {
        monto: Number(aporteForm.monto),
        notas: aporteForm.notas || null,
      });
      toast.success("Aporte registrado");
      setOpenAporte(null);
      setAporteForm({ monto: "", notas: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const cargarSugerida = async () => {
    try {
      const { data } = await api.get("/socios/utilidad-sugerida");
      setSugerida(data);
    } catch (e) { /* silencioso */ }
  };

  const distribuir = async () => {
    if (!distForm.utilidad_neta) return toast.error("Ingresa la utilidad neta");
    try {
      const { data } = await api.post("/socios/distribucion", {
        utilidad_neta: Number(distForm.utilidad_neta),
        notas: distForm.notas || null,
      });
      toast.success(`Distribuido entre ${data.reparto.length} socios`);
      setOpenDist(false);
      setDistForm({ utilidad_neta: "", notas: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const retirar = async () => {
    if (!retiroForm.monto) return toast.error("Monto obligatorio");
    try {
      await api.post(`/socios/${openRetiro.id}/retiro`, {
        monto: Number(retiroForm.monto),
        notas: retiroForm.notas || null,
      });
      toast.success("Retiro registrado");
      setOpenRetiro(null);
      setRetiroForm({ monto: "", notas: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const reinvertir = async () => {
    if (!window.confirm(`¿Cerrar el mes ${data.mes_actual} y reinvertir los sobrantes de todos los socios?`)) return;
    try {
      const { data: r } = await api.post(`/socios/reinvertir-sobrante/${data.mes_actual}`);
      toast.success(`Reinvertidos: ${r.reinversiones.length} socios`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  /** Reinvierte el disponible de UN socio (sin esperar el cierre de mes). */
  const reinvertirSocio = async (s) => {
    if (!s.disponible_mes) { toast.info("Este socio no tiene disponible para reinvertir."); return; }
    const monto = window.prompt(
      `Reinvertir disponible de ${s.nombre} (${data.mes_actual}).\n\n` +
      `Disponible: $${(s.disponible_mes || 0).toLocaleString("es-CO")}.\n` +
      `Escribe el monto a reinvertir (dejar vacío = todo el disponible):`,
      ""
    );
    if (monto === null) return; // canceló
    const payload = {};
    if (monto && monto.trim()) {
      const n = Number(monto.replace(/[^0-9]/g, ""));
      if (!n || n <= 0) { toast.error("Monto inválido"); return; }
      payload.monto = n;
    }
    try {
      const { data: r } = await api.post(`/socios/${s.id}/reinvertir-disponible`, payload);
      toast.success(`Reinvertido $${(r.monto_reinvertido || 0).toLocaleString("es-CO")}. Nuevo capital: $${(r.capital_actual || 0).toLocaleString("es-CO")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const verExtracto = async (s) => {
    setOpenExtracto(s);
    try {
      const { data } = await api.get(`/socios/${s.id}/extracto`);
      setExtracto(data);
    } catch { setExtracto(null); }
  };

  if (!isDirector) return <div className="p-8 text-center text-muted-foreground">Solo directores tienen acceso.</div>;
  if (!data) return <div className="p-8 text-center text-muted-foreground">Cargando...</div>;

  const socios = data.socios || [];
  const suma = data.suma_porcentajes || 0;

  return (
    <div>
      <header className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-primary mb-2">Sociedad</div>
          <h1 className="text-4xl font-display font-bold text-foreground flex items-center gap-3">
            <Users className="w-8 h-8 text-primary" /> Socios e inversiones
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Reparto de utilidades por participación fija.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="h-11" onClick={() => abrirCierreAnual()} data-testid="socios-cierre-btn">
            <Archive className="w-4 h-4 mr-2" /> Cierre anual
          </Button>
          <Button variant="outline" className="h-11" onClick={reinvertir} data-testid="socios-reinvertir-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Cerrar mes
          </Button>
          <Button variant="outline" className="h-11" onClick={() => { setOpenDist(true); cargarSugerida(); }} data-testid="socios-distribuir-btn">
            <TrendingUp className="w-4 h-4 mr-2" /> Distribuir utilidad
          </Button>
          <Button className="h-11 bg-primary hover:bg-primary/90" onClick={() => setOpenCrear(true)} data-testid="socios-crear-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo socio
          </Button>
        </div>
      </header>

      <UsageTip storageKey="socios-v2" steps={[
        "Cada socio tiene <b>capital propio</b> (aportes + reinversiones) y un <b>%</b> de participación sobre la utilidad que ese capital genera.",
        "Ejemplo: si invirtió $100M al 50% y ese capital generó $10M de utilidad, recibe $5M. El resto queda en la caja del negocio.",
        "Puedes <b>retirar</b> hasta el tope mensual del socio, o convertir su disponible en capital al instante con <b>Reinvertir disponible</b>.",
        "Al cerrar el mes, lo que quede sin retirar se reinvierte automático para todos.",
        "El socio ve solo su propia cuenta si tiene usuario enlazado.",
      ]} />

      {suma < 100 && (
        <div className="mb-6 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs">
          ⚠ La suma de participaciones activas es <b>{suma.toFixed(1)}%</b>. Si es &lt;100%, la utilidad restante queda sin repartir.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {socios.map((s) => (
          <div key={s.id} className={`p-6 rounded-xl border ${s.activo ? "bg-card border-border" : "bg-muted/30 border-border/40 opacity-60"}`} data-testid={`socio-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xl font-bold">{s.nombre}</div>
                {s.email && <div className="text-xs text-muted-foreground">{s.email}</div>}
              </div>
              <span className="text-2xl font-bold text-primary tabular-nums">{s.porcentaje}%</span>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Capital</div>
                <div className="text-lg font-semibold tabular-nums flex items-center gap-1">
                  <PiggyBank className="w-4 h-4 text-primary" /> {cop(s.capital_actual)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Disponible mes</div>
                <div className="text-lg font-semibold tabular-nums text-emerald-700">{cop(s.disponible_mes)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Tope mensual</div>
                <div className="text-lg font-semibold tabular-nums">{cop(s.retiro_mensual_tope)}</div>
              </div>
            </div>
            <div className="mt-4 flex gap-2 flex-wrap">
              <button onClick={() => verExtracto(s)} className="h-9 px-3 rounded-full border border-border hover:bg-muted/50 text-xs font-semibold" data-testid={`extracto-${s.id}`}>
                📊 Ver extracto
              </button>
              <button onClick={() => abrirEditar(s)} className="h-9 px-3 rounded-full border border-border hover:bg-muted/50 text-xs font-semibold flex items-center gap-1" data-testid={`editar-${s.id}`}>
                <Pencil className="w-3.5 h-3.5" /> Editar
              </button>
              <button onClick={() => setOpenAporte(s)} className="h-9 px-3 rounded-full border border-primary/40 text-primary hover:bg-primary/5 text-xs font-semibold flex items-center gap-1" data-testid={`aporte-${s.id}`}>
                <PiggyBank className="w-3.5 h-3.5" /> Aporte
              </button>
              {s.disponible_mes > 0 && (
                <>
                  <button onClick={() => setOpenRetiro(s)} className="h-9 px-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-semibold flex items-center gap-1" data-testid={`retiro-${s.id}`}>
                    <HandCoins className="w-3.5 h-3.5" /> Registrar retiro
                  </button>
                  <button onClick={() => reinvertirSocio(s)} className="h-9 px-3 rounded-full border border-emerald-500/40 text-emerald-700 hover:bg-emerald-50 text-xs font-semibold flex items-center gap-1" data-testid={`reinvertir-socio-${s.id}`} title="Convertir el disponible del mes en capital sin esperar el cierre">
                    <TrendingUp className="w-3.5 h-3.5" /> Reinvertir disponible
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        {socios.length === 0 && (
          <div className="col-span-2 p-12 text-center text-muted-foreground">
            <Users className="w-12 h-12 mx-auto opacity-40" />
            <div className="mt-3">Aún no hay socios. Crea el primero.</div>
          </div>
        )}
      </div>

      {/* Modal crear */}
      <Dialog open={openCrear} onOpenChange={setOpenCrear}>
        <DialogContent data-testid="socio-crear-modal">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nuevo socio</DialogTitle></DialogHeader>
          <div className="mt-4 space-y-3">
            <div>
              <Label>Nombre*</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="h-11 mt-1" data-testid="socio-nombre" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 mt-1" placeholder="Opcional" data-testid="socio-email" />
            </div>
            <div>
              <Label>Enlazar a usuario existente (para que vea su propia cuenta)</Label>
              <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                className="w-full h-11 mt-1 border border-border rounded-md px-2 bg-card text-sm" data-testid="socio-user-id">
                <option value="">— No enlazar aún —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Aporte inicial</Label>
                <MoneyInput value={form.aporte_inicial}
                  onValueChange={(v) => setForm({ ...form, aporte_inicial: v })} className="h-11 mt-1" data-testid="socio-aporte" />
              </div>
              <div>
                <Label>% Participación*</Label>
                <Input type="number" min="0.01" max="100" step="0.5" value={form.porcentaje}
                  onChange={(e) => setForm({ ...form, porcentaje: e.target.value })} className="h-11 mt-1 tabular-nums" data-testid="socio-pct" />
                <div className="text-[10px] text-muted-foreground mt-1">Actual usado: {suma.toFixed(1)}%</div>
              </div>
              <div>
                <Label>Tope retiro mensual</Label>
                <MoneyInput value={form.retiro_mensual_tope}
                  onValueChange={(v) => setForm({ ...form, retiro_mensual_tope: v })} className="h-11 mt-1" data-testid="socio-tope" />
              </div>
            </div>
            <Button onClick={crear} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="socio-save">Crear socio</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal distribuir */}
      <Dialog open={openDist} onOpenChange={setOpenDist}>
        <DialogContent data-testid="socio-dist-modal">
          <DialogHeader><DialogTitle className="font-display text-2xl">Distribuir utilidad</DialogTitle></DialogHeader>
          <div className="mt-4 space-y-3">
            <div className="p-3 rounded-lg bg-muted/40 text-xs text-muted-foreground">
              Se reparte según el % de cada socio activo. Mes: <b>{data.mes_actual}</b>.
            </div>

            {sugerida && (
              <div className="rounded-lg border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-3" data-testid="sugerida-card">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-primary">
                    <Sparkles className="w-3.5 h-3.5" /> Utilidad neta sugerida · {sugerida.mes}
                  </div>
                  <div className="flex gap-1">
                    {["causacion", "caja"].map((m) => (
                      <button key={m} onClick={() => setModoSugerido(m)}
                        className={`text-[10px] px-2 py-1 rounded-full font-bold uppercase ${modoSugerido === m ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
                        data-testid={`modo-${m}`}>
                        {m === "causacion" ? "Causación" : "Caja"}
                      </button>
                    ))}
                  </div>
                </div>
                {(() => {
                  const k = sugerida[modoSugerido] || {};
                  return (
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between"><span>Ventas ({k.pedidos} pedidos)</span><span className="tabular-nums font-semibold text-emerald-700">+{cop(k.ventas)}</span></div>
                      <div className="flex justify-between"><span>COGS (costo mercancía)</span><span className="tabular-nums text-rose-700">−{cop(k.cogs)}</span></div>
                      <div className="flex justify-between"><span>Comisiones vendedores</span><span className="tabular-nums text-rose-700">−{cop(k.comisiones)}</span></div>
                      <div className="flex justify-between"><span>Gastos del mes</span><span className="tabular-nums text-rose-700">−{cop(k.gastos)}</span></div>
                      <div className="border-t border-border/50 my-1"></div>
                      <div className="flex justify-between items-center">
                        <span className="font-bold">Utilidad neta {modoSugerido === "caja" ? "(caja)" : "(causación)"}</span>
                        <span className={`tabular-nums font-bold text-lg ${k.utilidad_neta_real < 0 ? "text-rose-600" : "text-primary"}`}>{cop(k.utilidad_neta_real)}</span>
                      </div>
                      <div className="pt-1">
                        <button type="button"
                          onClick={() => setDistForm({ ...distForm, utilidad_neta: String(Math.max(0, k.utilidad_neta_real)) })}
                          className="w-full h-8 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-[11px] font-bold uppercase tracking-widest"
                          data-testid="usar-sugerida-btn">
                          Usar este valor
                        </button>
                      </div>
                      <div className="text-[10px] text-muted-foreground pt-1">
                        {modoSugerido === "causacion"
                          ? "Incluye pedidos confirmados/despachados/entregados del mes (aunque el cliente aún no haya pagado)."
                          : "Solo pedidos donde el cliente ya pagó el 100%."}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <div>
              <Label>Utilidad neta del mes (COP)*</Label>
              <MoneyInput value={distForm.utilidad_neta}
                onValueChange={(v) => setDistForm({ ...distForm, utilidad_neta: v })}
                className="h-11 mt-1" data-testid="dist-utilidad" />
            </div>
            <div>
              <Label>Notas</Label>
              <Input value={distForm.notas} onChange={(e) => setDistForm({ ...distForm, notas: e.target.value })} className="h-11 mt-1" data-testid="dist-notas" />
            </div>
            {distForm.utilidad_neta && (() => {
              const activos = socios.filter((s) => s.activo);
              const capitalTotal = activos.reduce((acc, s) => acc + (s.capital_actual || 0), 0);
              const util = Number(distForm.utilidad_neta);
              const filas = activos.map((s) => {
                const cap = s.capital_actual || 0;
                const utilAtrib = capitalTotal > 0 ? util * (cap / capitalTotal) : 0;
                const monto = Math.round(utilAtrib * (s.porcentaje || 0) / 100);
                return { s, cap, utilAtrib, monto };
              });
              const totalSocios = filas.reduce((acc, f) => acc + f.monto, 0);
              const saldoDueño = util - totalSocios;
              return (
                <div className="p-3 bg-primary/5 rounded-md border border-primary/20 text-xs">
                  <div className="font-semibold mb-1">Simulación (proporcional al capital de cada socio):</div>
                  {capitalTotal <= 0 && (
                    <div className="text-rose-600 mb-1">⚠ Capital total = 0. Registra aportes antes de distribuir.</div>
                  )}
                  {filas.map(({ s, cap, utilAtrib, monto }) => (
                    <div key={s.id} className="flex justify-between mb-0.5">
                      <span title={`Utilidad atribuible a su capital: ${cop(Math.round(utilAtrib))}`}>
                        {s.nombre} ({s.porcentaje}%) · cap {cop(cap)}
                      </span>
                      <span className="tabular-nums font-semibold">{cop(monto)}</span>
                    </div>
                  ))}
                  <div className="mt-2 pt-2 border-t border-primary/20 flex justify-between">
                    <span className="font-semibold">Saldo del dueño (queda en caja)</span>
                    <span className="tabular-nums font-bold text-emerald-700">{cop(Math.max(0, saldoDueño))}</span>
                  </div>
                </div>
              );
            })()}
            <Button onClick={distribuir} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="dist-confirm">Distribuir</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal retiro */}
      <Dialog open={!!openRetiro} onOpenChange={(v) => !v && setOpenRetiro(null)}>
        <DialogContent data-testid="socio-retiro-modal">
          <DialogHeader><DialogTitle className="font-display text-2xl">Retiro de {openRetiro?.nombre}</DialogTitle></DialogHeader>
          <div className="mt-4 space-y-3">
            <div className="p-3 rounded-lg bg-muted/40 text-xs">
              Disponible: <b>{cop(openRetiro?.disponible_mes)}</b> · Tope mensual: <b>{cop(openRetiro?.retiro_mensual_tope)}</b>
            </div>
            <div>
              <Label>Monto*</Label>
              <MoneyInput value={retiroForm.monto}
                onValueChange={(v) => setRetiroForm({ ...retiroForm, monto: v })}
                className="h-11 mt-1" data-testid="retiro-monto" />
            </div>
            <div>
              <Label>Notas</Label>
              <Input value={retiroForm.notas} onChange={(e) => setRetiroForm({ ...retiroForm, notas: e.target.value })} className="h-11 mt-1" placeholder="Motivo, referencia bancaria, etc." data-testid="retiro-notas" />
            </div>
            <Button onClick={retirar} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="retiro-confirm">Registrar retiro</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal editar */}
      <Dialog open={!!openEditar} onOpenChange={(v) => !v && setOpenEditar(null)}>
        <DialogContent data-testid="socio-editar-modal">
          <DialogHeader><DialogTitle className="font-display text-2xl">Editar {openEditar?.nombre}</DialogTitle></DialogHeader>
          <div className="mt-4 space-y-3">
            <div>
              <Label>Nombre*</Label>
              <Input value={editForm.nombre} onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })} className="h-11 mt-1" data-testid="edit-nombre" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="h-11 mt-1" data-testid="edit-email" />
            </div>
            <div>
              <Label>Usuario enlazado</Label>
              <select value={editForm.user_id} onChange={(e) => setEditForm({ ...editForm, user_id: e.target.value })}
                className="w-full h-11 mt-1 border border-border rounded-md px-2 bg-card text-sm" data-testid="edit-user-id">
                <option value="">— No enlazar —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>% Participación</Label>
                <Input type="number" min="0.01" max="100" step="0.5" value={editForm.porcentaje}
                  onChange={(e) => setEditForm({ ...editForm, porcentaje: e.target.value })} className="h-11 mt-1 tabular-nums" data-testid="edit-pct" />
                {Number(editForm.porcentaje) !== Number(editForm._pct_original) && (
                  <div className="text-[10px] text-amber-700 mt-1">⚠ Requiere confirmación al guardar</div>
                )}
              </div>
              <div>
                <Label>Tope retiro mensual</Label>
                <MoneyInput value={editForm.retiro_mensual_tope}
                  onValueChange={(v) => setEditForm({ ...editForm, retiro_mensual_tope: v })} className="h-11 mt-1" data-testid="edit-tope" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!editForm.activo} onChange={(e) => setEditForm({ ...editForm, activo: e.target.checked })} data-testid="edit-activo" />
              Socio activo (recibe distribuciones)
            </label>
            <Button onClick={guardarEdicion} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="edit-save">Guardar cambios</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal aporte adicional */}
      <Dialog open={!!openAporte} onOpenChange={(v) => { if (!v) { setOpenAporte(null); setAporteForm({ monto: "", notas: "" }); } }}>
        <DialogContent data-testid="socio-aporte-modal">
          <DialogHeader><DialogTitle className="font-display text-2xl">Aporte de {openAporte?.nombre}</DialogTitle></DialogHeader>
          <div className="mt-4 space-y-3">
            <div className="p-3 rounded-lg bg-muted/40 text-xs">
              Capital actual: <b>{cop(openAporte?.capital_actual)}</b>. Este aporte se sumará al capital.
            </div>
            <div>
              <Label>Monto del aporte*</Label>
              <MoneyInput value={aporteForm.monto}
                onValueChange={(v) => setAporteForm({ ...aporteForm, monto: v })}
                className="h-11 mt-1" data-testid="aporte-monto" />
            </div>
            <div>
              <Label>Notas</Label>
              <Input value={aporteForm.notas} onChange={(e) => setAporteForm({ ...aporteForm, notas: e.target.value })}
                className="h-11 mt-1" placeholder="Referencia, motivo, etc." data-testid="aporte-notas" />
            </div>
            <Button onClick={registrarAporte} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="aporte-confirm">Registrar aporte</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal extracto */}
      <Dialog open={!!openExtracto} onOpenChange={(v) => { if (!v) { setOpenExtracto(null); setExtracto(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="socio-extracto-modal">
          <DialogHeader><DialogTitle className="font-display text-2xl">Extracto: {openExtracto?.nombre}</DialogTitle></DialogHeader>
          {!extracto ? (
            <div className="text-center text-muted-foreground py-8">Cargando...</div>
          ) : (
            <div className="mt-4">
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="p-3 rounded bg-muted/40">
                  <div className="text-[10px] uppercase text-muted-foreground">Capital</div>
                  <div className="text-lg font-bold tabular-nums">{cop(extracto.socio.capital_actual)}</div>
                </div>
                <div className="p-3 rounded bg-muted/40">
                  <div className="text-[10px] uppercase text-muted-foreground">Disponible mes</div>
                  <div className="text-lg font-bold tabular-nums text-emerald-700">{cop(extracto.socio.disponible_mes)}</div>
                </div>
                <div className="p-3 rounded bg-muted/40">
                  <div className="text-[10px] uppercase text-muted-foreground">Participación</div>
                  <div className="text-lg font-bold tabular-nums text-primary">{extracto.socio.porcentaje}%</div>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground border-b border-border">
                <div className="col-span-2">Fecha</div>
                <div className="col-span-2">Tipo</div>
                <div className="col-span-4">Notas</div>
                <div className="col-span-2">Mes</div>
                <div className="col-span-2 text-right">Monto</div>
              </div>
              {extracto.movimientos.map((m) => (
                <div key={m.id} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs border-b border-border/40 hover:bg-muted/20">
                  <div className="col-span-2 text-muted-foreground">{(m.fecha || "").slice(0, 10)}</div>
                  <div className="col-span-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                      m.tipo === "aporte" ? "bg-blue-100 text-blue-800" :
                      m.tipo === "distribucion" ? "bg-emerald-100 text-emerald-800" :
                      m.tipo === "retiro" ? "bg-rose-100 text-rose-800" :
                      "bg-primary/10 text-primary"
                    }`}>{m.tipo}</span>
                  </div>
                  <div className="col-span-4 truncate text-muted-foreground">{m.notas}</div>
                  <div className="col-span-2 text-muted-foreground">{m.mes}</div>
                  <div className={`col-span-2 text-right tabular-nums font-semibold ${m.tipo === "retiro" ? "text-rose-600" : ""}`}>
                    {m.tipo === "retiro" ? "-" : "+"}{cop(m.monto)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* -------- Cierre Anual -------- */}
      <Dialog open={openCierre} onOpenChange={setOpenCierre}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="cierre-anual-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-primary" /> Cierre anual de la sociedad
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center gap-3 mb-4">
            <Label className="text-xs">Año</Label>
            <Input
              type="number"
              min="2020" max="2100"
              value={cierreYear}
              onChange={(e) => setCierreYear(Number(e.target.value) || new Date().getFullYear())}
              className="h-9 w-28"
              data-testid="cierre-year-input"
            />
            <Button size="sm" onClick={() => abrirCierreAnual(cierreYear)} disabled={cierreLoading} data-testid="cierre-generar-btn">
              {cierreLoading ? "Calculando…" : "Generar"}
            </Button>
          </div>

          {cierreLoading && (
            <div className="p-8 text-center text-sm text-muted-foreground">Calculando patrimonio…</div>
          )}

          {!cierreLoading && cierre && (
            <div className="space-y-6">
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-4 rounded-xl border border-border bg-card">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Capital total</div>
                  <div className="text-lg font-semibold tabular-nums mt-1">{cop(cierre.capital_total)}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">Aportes + reinversiones – retiros</div>
                </div>
                <div className="p-4 rounded-xl border border-border bg-card">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Utilidades pendientes</div>
                  <div className="text-lg font-semibold tabular-nums mt-1 text-emerald-700">{cop(cierre.utilidades_pendientes_total)}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">Distribuidas y aún no retiradas</div>
                </div>
                <div className="p-4 rounded-xl border border-border bg-card">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Inventario (a costo)</div>
                  <div className="text-lg font-semibold tabular-nums mt-1 text-primary">{cop(cierre.inventario_valor)}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{(cierre.inventario_unidades || 0).toLocaleString("es-CO")} unidades en bodega</div>
                </div>
                <div className="p-4 rounded-xl border border-primary/50 bg-primary/5">
                  <div className="text-[10px] uppercase tracking-widest text-primary font-bold">Patrimonio total</div>
                  <div className="text-xl font-bold tabular-nums mt-1 text-primary">{cop(cierre.patrimonio_total)}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">Capital + pendientes + inventario</div>
                </div>
              </div>

              {/* Explicación */}
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs leading-relaxed">
                💡 <b>Cómo funciona:</b> Cada socio recibe una parte del patrimonio total <b>proporcional a su capital actual</b>. El inventario valorado a costo se reparte con la misma proporción. Este reporte es solo informativo — no crea movimientos ni afecta el capital.
              </div>

              {/* Tabla por socio */}
              <div className="border border-border rounded-xl overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/50 text-[10px] uppercase font-bold text-muted-foreground">
                  <div className="col-span-3">Socio</div>
                  <div className="col-span-1 text-right">% Capital</div>
                  <div className="col-span-2 text-right">Capital</div>
                  <div className="col-span-2 text-right">Pendiente</div>
                  <div className="col-span-2 text-right">Parte inventario</div>
                  <div className="col-span-2 text-right">Patrimonio</div>
                </div>
                {(cierre.socios || []).map((s) => (
                  <div key={s.id} className="grid grid-cols-12 gap-2 px-3 py-2.5 text-xs border-t border-border/40 hover:bg-muted/20" data-testid={`cierre-row-${s.id}`}>
                    <div className="col-span-3 font-semibold flex items-center gap-2">
                      {s.nombre}
                      {!s.activo && <span className="text-[9px] uppercase text-muted-foreground/60">inactivo</span>}
                    </div>
                    <div className="col-span-1 text-right tabular-nums">{(s.proporcion_capital || 0).toFixed(1)}%</div>
                    <div className="col-span-2 text-right tabular-nums">{cop(s.capital_actual)}</div>
                    <div className="col-span-2 text-right tabular-nums text-emerald-700">{cop(s.utilidad_pendiente)}</div>
                    <div className="col-span-2 text-right tabular-nums text-primary">{cop(s.parte_inventario)}</div>
                    <div className="col-span-2 text-right tabular-nums font-bold">{cop(s.patrimonio_estimado)}</div>
                  </div>
                ))}
                {(cierre.socios || []).length === 0 && (
                  <div className="p-6 text-center text-sm text-muted-foreground">No hay socios registrados aún.</div>
                )}
              </div>

              {/* Movimientos del año (resumen por socio) */}
              <div>
                <div className="text-xs uppercase font-bold text-muted-foreground mb-2">Movimientos del año {cierre.year}</div>
                <div className="border border-border rounded-xl overflow-hidden">
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/40 text-[10px] uppercase font-bold text-muted-foreground">
                    <div className="col-span-4">Socio</div>
                    <div className="col-span-2 text-right">Aportes</div>
                    <div className="col-span-2 text-right">Distribuciones</div>
                    <div className="col-span-2 text-right">Retiros</div>
                    <div className="col-span-2 text-right">Reinversiones</div>
                  </div>
                  {(cierre.socios || []).map((s) => (
                    <div key={s.id} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs border-t border-border/40">
                      <div className="col-span-4">{s.nombre}</div>
                      <div className="col-span-2 text-right tabular-nums">{cop(s.aportes_year)}</div>
                      <div className="col-span-2 text-right tabular-nums text-emerald-700">{cop(s.distribuciones_year)}</div>
                      <div className="col-span-2 text-right tabular-nums text-rose-600">{cop(s.retiros_year)}</div>
                      <div className="col-span-2 text-right tabular-nums">{cop(s.reinversiones_year)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
